import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { TIERS } from "@/lib/links";
import { SlotPicker } from "@/components/booking/SlotPicker";
import { BookingDetails } from "@/components/booking/BookingDetails";
import { PrepChecklist } from "@/components/PrepChecklist";
import { BookingDone } from "@/components/booking/BookingDone";
import { BookingStepper, TierPicker, BookingSummary } from "@/components/booking/BookingSteps";
import { memberFor, memberPriceFor, payLabelFor, canProceed } from "@/components/booking/bookingHelpers";
import { useMemberCheck } from "@/lib/useMemberCheck";
import { getRememberedEmail } from "@/lib/memberEmail";

export default function Booking() {
  const [params] = useSearchParams();
  const preset = TIERS.find((t) => t.id === params.get("service"));
  const [step, setStep] = useState(preset ? 1 : 0);
  const [tier, setTier] = useState(preset || null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: getRememberedEmail(), phone: "", meeting_method: "Phone call", notes: "", gift_code: "", gift: null });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const member = memberFor(useMemberCheck(form.email), tier);
  const memberPrice = memberPriceFor(tier, member);
  const canNext = canProceed(step, tier, slot, form);

  const submit = async () => {
    setBusy(true);
    try {
      const { gift: _gift, ...body } = form;
      const r = await api.post("/bookings", { service_id: tier.id, start_at: slot.start_at, ...body }, { timeout: 60000 });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed — please try again or call 310-422-0669");
      if (e?.response?.status === 409) { setSlot(null); setStep(1); }
    } finally { setBusy(false); }
  };

  if (done) return <BookingDone done={done} tier={tier} slot={slot} />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8 dg-reveal">
        <span className="dg-eyebrow">Booking</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-display">Book a phone or video session.</h1>
        <p className="mt-3 text-slate-300">Pick a session, pick an open time from David's calendar, tell us how to reach you. Nothing to pay today — you get a secure card link after the session.</p>
        <p className="mt-2 text-sm text-slate-500" data-testid="booking-onsite-note">If it turns out the problem can't be fully fixed remotely, David can send a technician to finish it on-site in the LA area for a separate service charge — you'll get the price first.</p>
      </div>

      <BookingStepper step={step} />

      <div className="dg-card rounded-2xl p-6 sm:p-8">
        {step === 0 && <TierPicker value={tier} onChange={setTier} />}
        {step === 1 && <SlotPicker serviceId={tier.id} value={slot} onChange={setSlot} />}
        {step === 2 && <BookingDetails form={form} setForm={setForm} price={tier.price} tierId={tier.id} member={member} memberPrice={memberPrice} />}

        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || busy} className="dg-btn-ghost rounded-full px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="booking-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <BookingSummary tier={tier} slot={slot} member={member} memberPrice={memberPrice} />
          {step < 2 ? (
            <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext} className="dg-btn-primary rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="booking-next">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={!canNext || busy} className="dg-btn-primary rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="booking-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? "Booking…" : form.gift ? "Book with gift code" : payLabelFor(member, memberPrice)}
            </button>
          )}
        </div>
      </div>

      <div className="mt-10"><PrepChecklist /></div>
    </div>
  );
}
